import type {
    ApiDocument,
    ApiDocumentType,
    ApiDocumentTypeOption,
    ApiFolderedDocument,
    ApiSectionGroup,
} from "../../shared/interfaces";
import type { TypedFolderScope } from "../../shared/folderScopes";
import { PROJECT_ALIAS_DOCUMENT_TYPES } from "./documentTypes";
import type { ProjectQueryValue, ProjectTransport } from "./transport";
import {
    createProjectRowDropReporter,
    failValidation,
    optionalField,
    requireArray,
    requireBoolean,
    requireNonNegativeInteger,
    requireNullableString,
    requireNullableWireString,
    requireOptionalNullableWireString,
    requireOptionalWireString,
    requireRecord,
    requireString,
    requireWireString,
    validateResponseListRows,
} from "./validation";

const DOCUMENTS_ENDPOINT = "/api/ai/documents";
const DOCUMENT_TYPES_ENDPOINT = "/api/ai/documents/types";
const SECTION_GROUPS_BY_TYPE_ENDPOINT = "/api/ai/document/section_groups_by_type";
const DOCUMENTS_PAGE_SIZE = 1000;

export type DocumentQuery = Readonly<Record<string, ProjectQueryValue>>;
export type ProjectDocumentRowValidator<T extends { id: string }> = (
    value: unknown,
    field: string,
) => T;

function validateOptionalDocumentFields(
    row: Record<string, unknown>,
    endpoint: string,
    field: string,
): void {
    const prefix = field.length === 0 ? "" : `${field}.`;
    const fileValue = optionalField(row, "file");
    if (fileValue !== undefined) {
        const file = requireRecord(fileValue, endpoint, `${prefix}file`);
        requireString(file.id, endpoint, `${prefix}file.id`);
    }
    requireOptionalWireString(optionalField(row, "status"), endpoint, `${prefix}status`);
    requireOptionalWireString(
        optionalField(row, "error_message"),
        endpoint,
        `${prefix}error_message`,
    );
}

function validateDocument(value: unknown, field: string): ApiDocument {
    const row = requireRecord(value, DOCUMENTS_ENDPOINT, field);
    requireString(row.id, DOCUMENTS_ENDPOINT, `${field}.id`);
    requireString(row.document_type, DOCUMENTS_ENDPOINT, `${field}.document_type`);
    // `title` is display text, not an identity. The column is NOT NULL but a real
    // tenant holds documents titled "", and rejecting those blanked the whole Home
    // screen. `validateBatchDocument` below already read it this way; these three
    // sites did not, which is why the same document passed one route and failed
    // another.
    requireWireString(row.title, DOCUMENTS_ENDPOINT, `${field}.title`);
    const documentSubtype = optionalField(row, "document_subtype");
    if (documentSubtype !== undefined) {
        requireNullableWireString(documentSubtype, DOCUMENTS_ENDPOINT, `${field}.document_subtype`);
    }
    validateOptionalDocumentFields(row, DOCUMENTS_ENDPOINT, field);
    return row as unknown as ApiDocument;
}

function validateFolderedDocument(value: unknown, field: string): ApiFolderedDocument {
    const row = requireRecord(value, DOCUMENTS_ENDPOINT, field);
    requireString(row.id, DOCUMENTS_ENDPOINT, `${field}.id`);
    requireString(row.document_type, DOCUMENTS_ENDPOINT, `${field}.document_type`);
    requireWireString(row.title, DOCUMENTS_ENDPOINT, `${field}.title`);
    requireBoolean(row.is_active, DOCUMENTS_ENDPOINT, `${field}.is_active`);
    requireWireString(row.created_at, DOCUMENTS_ENDPOINT, `${field}.created_at`);
    requireBoolean(row.in_library, DOCUMENTS_ENDPOINT, `${field}.in_library`);
    requireNullableString(row.folder_id, DOCUMENTS_ENDPOINT, `${field}.folder_id`);
    validateOptionalDocumentFields(row, DOCUMENTS_ENDPOINT, field);
    return row as unknown as ApiFolderedDocument;
}

type ProjectSectionGroupType = "drawing" | "csi" | "shop_drawing" | "azdot" | "txdot";

export interface ProjectSectionGroupsByTypeResponse {
    documents: ApiDocument[];
    section_groups: Record<string, ApiSectionGroup[]>;
}

function validateBatchDocument(value: unknown, field: string): ApiDocument {
    const document = requireRecord(value, SECTION_GROUPS_BY_TYPE_ENDPOINT, field);
    requireString(document.id, SECTION_GROUPS_BY_TYPE_ENDPOINT, `${field}.id`);
    requireString(
        document.document_type,
        SECTION_GROUPS_BY_TYPE_ENDPOINT,
        `${field}.document_type`,
    );
    requireWireString(document.title, SECTION_GROUPS_BY_TYPE_ENDPOINT, `${field}.title`);
    validateOptionalDocumentFields(document, SECTION_GROUPS_BY_TYPE_ENDPOINT, field);
    return document as unknown as ApiDocument;
}

function validateSectionGroup(value: unknown, documentId: string, field: string): ApiSectionGroup {
    const group = requireRecord(value, SECTION_GROUPS_BY_TYPE_ENDPOINT, field);
    requireString(group.id, SECTION_GROUPS_BY_TYPE_ENDPOINT, `${field}.id`);
    requireWireString(group.group_code, SECTION_GROUPS_BY_TYPE_ENDPOINT, `${field}.group_code`);
    requireOptionalNullableWireString(
        optionalField(group, "display_code"),
        SECTION_GROUPS_BY_TYPE_ENDPOINT,
        `${field}.display_code`,
    );
    requireWireString(group.group_title, SECTION_GROUPS_BY_TYPE_ENDPOINT, `${field}.group_title`);
    const groupDocumentId = requireString(
        group.document_id,
        SECTION_GROUPS_BY_TYPE_ENDPOINT,
        `${field}.document_id`,
    );
    if (groupDocumentId !== documentId) {
        return failValidation(SECTION_GROUPS_BY_TYPE_ENDPOINT, `${field}.document_id`);
    }
    if (group.start_page_in_file !== null) {
        requireNonNegativeInteger(
            group.start_page_in_file,
            SECTION_GROUPS_BY_TYPE_ENDPOINT,
            `${field}.start_page_in_file`,
        );
    }
    requireNonNegativeInteger(
        group.revision_number,
        SECTION_GROUPS_BY_TYPE_ENDPOINT,
        `${field}.revision_number`,
    );
    requireBoolean(group.is_active, SECTION_GROUPS_BY_TYPE_ENDPOINT, `${field}.is_active`);
    requireOptionalNullableWireString(
        optionalField(group, "status"),
        SECTION_GROUPS_BY_TYPE_ENDPOINT,
        `${field}.status`,
    );
    requireOptionalNullableWireString(
        optionalField(group, "processing_status"),
        SECTION_GROUPS_BY_TYPE_ENDPOINT,
        `${field}.processing_status`,
    );
    requireWireString(group.discipline, SECTION_GROUPS_BY_TYPE_ENDPOINT, `${field}.discipline`);
    const revision = optionalField(group, "revision");
    if (revision !== undefined && revision !== null) {
        const revisionRecord = requireRecord(
            revision,
            SECTION_GROUPS_BY_TYPE_ENDPOINT,
            `${field}.revision`,
        );
        requireString(revisionRecord.id, SECTION_GROUPS_BY_TYPE_ENDPOINT, `${field}.revision.id`);
        requireWireString(
            revisionRecord.name,
            SECTION_GROUPS_BY_TYPE_ENDPOINT,
            `${field}.revision.name`,
        );
    }
    requireOptionalNullableWireString(
        optionalField(group, "updated_at"),
        SECTION_GROUPS_BY_TYPE_ENDPOINT,
        `${field}.updated_at`,
    );
    const fileValue = optionalField(group, "file");
    if (fileValue !== undefined) {
        const file = requireRecord(fileValue, SECTION_GROUPS_BY_TYPE_ENDPOINT, `${field}.file`);
        requireString(file.id, SECTION_GROUPS_BY_TYPE_ENDPOINT, `${field}.file.id`);
    }
    return group as unknown as ApiSectionGroup;
}

function validateSectionGroupsByTypeResponse(value: unknown): ProjectSectionGroupsByTypeResponse {
    const payload = requireRecord(value, SECTION_GROUPS_BY_TYPE_ENDPOINT, "response");
    const documentRows = requireArray(
        payload.documents,
        SECTION_GROUPS_BY_TYPE_ENDPOINT,
        "documents",
    );
    const sectionGroups = requireRecord(
        payload.section_groups,
        SECTION_GROUPS_BY_TYPE_ENDPOINT,
        "section_groups",
    );
    const reporter = createProjectRowDropReporter();
    const documents = validateResponseListRows(
        documentRows,
        SECTION_GROUPS_BY_TYPE_ENDPOINT,
        "document",
        validateBatchDocument,
        reporter,
        "documents",
    );
    const validatedSectionGroups: Record<string, ApiSectionGroup[]> = {};
    let changed = documents !== documentRows;
    for (const document of documents) {
        const groupField = `section_groups.${document.id}`;
        const groups = requireArray(
            sectionGroups[document.id],
            SECTION_GROUPS_BY_TYPE_ENDPOINT,
            groupField,
        );
        const validatedGroups = validateResponseListRows(
            groups,
            SECTION_GROUPS_BY_TYPE_ENDPOINT,
            "section group",
            (group, field) => validateSectionGroup(group, document.id, field),
            reporter,
            groupField,
        );
        validatedSectionGroups[document.id] = validatedGroups;
        changed ||= validatedGroups !== groups;
    }
    if (!changed) return payload as unknown as ProjectSectionGroupsByTypeResponse;
    return {
        ...payload,
        documents,
        section_groups: validatedSectionGroups,
    } as unknown as ProjectSectionGroupsByTypeResponse;
}

export async function fetchProjectDocumentPages<T extends { id: string }>(
    transport: ProjectTransport,
    path: string,
    query: DocumentQuery,
    validateRow: ProjectDocumentRowValidator<T>,
    signal?: AbortSignal,
): Promise<T[]> {
    const documents: T[] = [];
    const seenIds = new Set<string>();
    let offset = 0;

    while (true) {
        const response = await transport.request<unknown>({
            method: "GET",
            path,
            query: { ...query, limit: DOCUMENTS_PAGE_SIZE, offset },
            signal,
        });
        const page = requireArray(response, path, "response");
        if (page.length === 0) break;

        let unseenCount = 0;
        const validRows = validateResponseListRows(page, path, "document", validateRow);
        for (const document of validRows) {
            if (seenIds.has(document.id)) continue;
            seenIds.add(document.id);
            documents.push(document);
            unseenCount += 1;
        }

        if (unseenCount === 0 || page.length < DOCUMENTS_PAGE_SIZE) break;
        offset += DOCUMENTS_PAGE_SIZE;
    }

    return documents;
}

export interface ProjectDocumentApi {
    getDocumentById(documentId: string, signal?: AbortSignal): Promise<ApiDocument>;
    getDocuments(
        documentType?: ApiDocumentType | ApiDocumentType[],
        signal?: AbortSignal,
    ): Promise<ApiDocument[]>;
    getDocumentsByType(
        type?: "drawing" | "csi" | "shop_drawing",
        signal?: AbortSignal,
    ): Promise<ApiDocument[]>;
    /** `view: "slim"` asks for list rows: the full rows (signed `file.fileUrl` included)
     *  without `metadata`, and `identity_verification` only when it disagrees. A server
     *  that predates the parameter ignores it and answers full rows, which every reader
     *  also accepts. */
    getSectionGroupsByType(
        type: ProjectSectionGroupType,
        signal?: AbortSignal,
        revisionId?: string,
        view?: "slim",
        /** `fileUrls: false` omits every row's signed `file.fileUrl`. Only readers
         *  that sign on demand (sheet download/export via `FileApi.getFile`) may ask
         *  for it; the viewer still needs `row.file.fileUrl`. A server that predates
         *  the parameter ignores it and answers rows with URLs. */
        options?: { fileUrls?: boolean },
    ): Promise<ProjectSectionGroupsByTypeResponse>;
    getAllAliasDocuments(signal?: AbortSignal): Promise<ApiDocument[]>;
    getDocumentTypes(signal?: AbortSignal): Promise<ApiDocumentTypeOption[]>;
    /** A folder's own files, or a top-level folder's files outside any folder: Additional docs'
     *  without `scope`, a type's with it (`?document_type=` and `folder_id` or `root_only`). */
    getFolderedDocuments(
        folderId?: string,
        signal?: AbortSignal,
        scope?: TypedFolderScope,
    ): Promise<ApiFolderedDocument[]>;
}

export function createDocumentApi(transport: ProjectTransport): ProjectDocumentApi {
    return {
        async getDocumentById(documentId, signal) {
            const path = `/api/ai/document/${documentId}`;
            const response = await transport.request<unknown>({ method: "GET", path, signal });
            const row = requireRecord(response, path, "response");
            requireString(row.id, path, "id");
            requireString(row.document_type, path, "document_type");
            requireWireString(row.title, path, "title");
            validateOptionalDocumentFields(row, path, "");
            return row as unknown as ApiDocument;
        },

        async getDocuments(documentType, signal) {
            const query: Record<string, ProjectQueryValue> = {};
            if (documentType) {
                query.document_type = Array.isArray(documentType)
                    ? documentType.join(",")
                    : documentType;
            }
            return fetchProjectDocumentPages(
                transport,
                DOCUMENTS_ENDPOINT,
                query,
                validateDocument,
                signal,
            );
        },

        async getDocumentsByType(type, signal) {
            const path = "/api/ai/document/by_type";
            const response = await transport.request<unknown>({
                method: "GET",
                path,
                query: { type },
                signal,
            });
            const rows = requireArray(response, path, "response");
            return validateResponseListRows(rows, path, "document", (value, field) => {
                const row = requireRecord(value, path, field);
                requireString(row.id, path, `${field}.id`);
                requireString(row.document_type, path, `${field}.document_type`);
                requireWireString(row.title, path, `${field}.title`);
                return row as unknown as ApiDocument;
            });
        },

        async getSectionGroupsByType(type, signal, revisionId, view, options) {
            const response = await transport.request<unknown>({
                method: "GET",
                path: SECTION_GROUPS_BY_TYPE_ENDPOINT,
                query: {
                    type,
                    ...(revisionId ? { revision_id: revisionId } : {}),
                    ...(view ? { view } : {}),
                    ...(options?.fileUrls === false ? { file_urls: false } : {}),
                },
                signal,
            });
            return validateSectionGroupsByTypeResponse(response);
        },

        getAllAliasDocuments(signal) {
            const aliasTypes: readonly ApiDocumentType[] = PROJECT_ALIAS_DOCUMENT_TYPES;
            return fetchProjectDocumentPages(
                transport,
                DOCUMENTS_ENDPOINT,
                { document_type: aliasTypes.join(",") },
                validateDocument,
                signal,
            );
        },

        async getDocumentTypes(signal) {
            const response = await transport.request<unknown>({
                method: "GET",
                path: DOCUMENT_TYPES_ENDPOINT,
                signal,
            });
            const payload = requireRecord(response, DOCUMENT_TYPES_ENDPOINT, "response");
            const rawTypes = optionalField(payload, "types");
            if (rawTypes === undefined || rawTypes === null) return [];
            const rows = requireArray(rawTypes, DOCUMENT_TYPES_ENDPOINT, "types");
            return validateResponseListRows(
                rows,
                DOCUMENT_TYPES_ENDPOINT,
                "document type",
                (value, field) => {
                    const row = requireRecord(value, DOCUMENT_TYPES_ENDPOINT, field);
                    requireString(row.value, DOCUMENT_TYPES_ENDPOINT, `${field}.value`);
                    requireOptionalWireString(
                        optionalField(row, "label"),
                        DOCUMENT_TYPES_ENDPOINT,
                        `${field}.label`,
                    );
                    return row as unknown as ApiDocumentTypeOption;
                },
                undefined,
                "types",
            );
        },

        getFolderedDocuments(folderId, signal, scope) {
            // A type's files at its top level are `root_only`, as Additional docs' are.
            const place = folderId ? { folder_id: folderId } : { root_only: true };
            const query = scope
                ? { document_type: scope, ...place }
                : { ...(folderId ? {} : { library: true }), ...place };
            return fetchProjectDocumentPages(
                transport,
                DOCUMENTS_ENDPOINT,
                query,
                validateFolderedDocument,
                signal,
            );
        },
    };
}
